"use client";

/**
 * Rail panel: list of stale org-profile values that need resolution.
 *
 * Extracted from `compass-intake.jsx:774-793` (`.cs-updates`, `.cs-updlist`).
 */

import { ProvenanceDot } from "../data-display/ProvenanceDot";
import { Alert, ArrowR } from "../lib/icons";
import type { ProvenanceType } from "../lib/provenance";

export interface StaleValue {
  n: number;
  label: string;
  oldVal: string;
  newVal: string;
  by: string;
  when: string;
  type?: ProvenanceType;
}

export interface UpdatesPanelProps {
  staleValues: StaleValue[];
  onOpenSource?: (n: number) => void;
}

export function UpdatesPanel({ staleValues, onOpenSource }: UpdatesPanelProps) {
  return (
    <div className="flex flex-col gap-3 p-[18px] box-border w-full">
      {/* Banner */}
      <div className="flex gap-2 items-start p-[10px_12px] bg-(--stale-bg) border border-(--stale-border) rounded-(--radius-field) text-[12px] leading-[1.45] text-(--status-warning-text)">
        <span className="text-(--status-warning-text) shrink-0 mt-px">
          <Alert size={14} />
        </span>
        <span>
          <b className="font-bold">
            {staleValues.length} value{staleValues.length > 1 ? "s" : ""} need
            updating.
          </b>{" "}
          Your org profile has newer data. Open one to review, or leave it as
          is.
        </span>
      </div>

      {/* List */}
      <div className="flex flex-col gap-2">
        {staleValues.map((v) => (
          <button
            key={v.n}
            type="button"
            className={[
              "flex items-center gap-[11px] w-full text-left",
              "bg-(--bg-white) border border-(--stale)",
              "shadow-[0_0_0_3px_var(--stale-bg)]",
              "rounded-(--radius-field) px-[13px] py-[11px]",
              "cursor-pointer transition-[border-color,box-shadow] duration-150",
              "font-[family-name:var(--font-body)]",
              "hover:border-(--border-strong) hover:shadow-(--shadow-xs)",
            ].join(" ")}
            onClick={() => onOpenSource?.(v.n)}
          >
            <ProvenanceDot type={v.type ?? "estimated"} />
            <span className="flex-1 min-w-0 flex flex-col">
              <span className="text-[13px] font-semibold text-(--fg-primary) truncate">
                {v.label}
              </span>
              <span className="text-[11px] text-(--fg-muted) mt-px">
                Updated {v.when} · {v.by}
              </span>
            </span>
            <span className="shrink-0 inline-flex items-center gap-[5px] text-[12px] text-(--fg-secondary)">
              <s className="text-(--fg-subtle)">{v.oldVal}</s>
              <span className="text-(--status-warning-text)">
                <ArrowR size={12} />
              </span>
              <b className="font-bold text-(--fg-primary)">{v.newVal}</b>
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}
